import { useTaskStore } from "@core/stores/task.store"

// ================================
// TYPES & INTERFACES
// ================================

/**
 * Options pour le lancement de la tâche source.rebuild_data.request
 */
export interface UseRebuildDataTaskOptions {
    /**
     * Affiche une notification lors de la progression de la tâche
     * @default true
     */
    notification?: boolean
    /**
     * Supprime automatiquement la tâche du suivi à la fin
     * @default true
     */
    autoDelete?: boolean
    /**
     * Libellé affiché pour la tâche
     * @default "Récupération des données de la source"
     */
    taskname?: string
    /**
     * Callback appelé lorsque la tâche reçoit des données
     */
    onReceived?: () => void
    /**
     * Callback appelé lorsque la tâche réussit
     */
    onSuccess?: (data: unknown) => void
    /**
     * Callback appelé en cas d'erreur de la tâche
     */
    onError?: (error: unknown) => void
}

// ================================
// COMPOSABLE
// ================================

/**
 * Composable pour lancer la tâche source.rebuild_data.request.
 * Centralise le lancement et le suivi de la tâche de reconstruction des données.
 *
 * @param options - Options de configuration (notification, autoDelete, callbacks)
 * @returns Fonction pour lancer la tâche avec un sourceId
 */
export function useRebuildDataTask(options: UseRebuildDataTaskOptions = {}) {
    const taskStore = useTaskStore()

    const {
        notification = true,
        autoDelete = true,
        taskname = "Récupération des données de la source",
        onReceived,
        onSuccess,
        onError,
    } = options

    /**
     * Lance la tâche de rebuild des données pour une source
     * @param sourceId - Identifiant de la source
     */
    async function launchRebuildDataTask(sourceId: string): Promise<void> {
        try {
            await taskStore.launchTask(
                "source.rebuild_data.request",
                { _id_source: sourceId },
                undefined,
                {
                    taskname,
                    notification,
                    autoDelete,
                    onReceived,
                    onSuccess,
                    onError,
                }
            )
        } catch (error) {
            console.error(
                "Erreur lors de la création de la tâche de rebuild:",
                error
            )
            onError?.(error)
        }
    }

    return { launchRebuildDataTask }
}
